import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../../AuthContextWrapper";
import { Box, Button, Typography } from "@mui/material";
import chill1 from "../../assets/chill-1x.png";
import chill15 from "../../assets/chill-1.5x.png";
import chill2 from "../../assets/chill-2x.png";
import chill3 from "../../assets/chill-3x.png";
import { Estate } from "../../../models/Estate";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";

export enum Outcomes {
  getStarted = "Get Started",
  alreadyStarted = "I already started probate",
}

export const WelcomeScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, setActiveEstate, user } = useAuthContext()
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (answer: string): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");
    if (!user?.id) {
      console.error("no user id");
      return false;
    }

    const estate = activeEstate || new Estate();
    if (user?.profile?.countryId) {
      // preset countryId
      const countryId = parseInt(user.profile.countryId, 10);
      if (!isNaN(countryId)) {
        estate.countryId = countryId;
      }
    }
    estate.countryId = estate.countryId || undefined // clear it out if it's not set.  null will cause an error
    
    if (user?.profile?.stateId) {
      // in some cases the stateId will be selected during sign up and can immediately be set on the estate here
      const stateId = parseInt(user.profile.stateId, 10);
      if (!isNaN(stateId)) {
        estate.stateId = stateId;
      }
    }
    estate.stateId = estate.stateId || undefined // clear it out if it's not set.  0 will cause countryId to be required and we probably don't know either yet
    estate.alreadyStarted = answer === Outcomes.alreadyStarted;

    try {
      await estate.saveEstate(user.id);
      setActiveEstate(estate);
    } catch (error) {
      throw new Error("could not save data");
    }

    flow?.navigateToNext(currentScreen, answer, navigate);
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper hideSectionHeading={true} centerContent={true}>
      <>
        <Box
          sx={{
            my: 2,
            textAlign: "center",
          }}
        >
          <img
            srcSet={`${chill15} 1.5x, ${chill2} 2x, ${chill3} 3x`}
            src={chill1}
            alt="Chill"
            title="Chill"
          />
        </Box>
        <Box
          sx={{
            my: 2,
            textAlign: "center",
          }}
        >
          <Typography component="h2" variant="h3">
            Welcome to the Atticus family{user?.first ? `, ${user?.first}` : ""}!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              my: 2,
              mx: "auto",
              width: "80%",
              maxWidth: 480,
            }}
          >
            We’re going to ask you a few simple questions to help us understand
            where you’re at in the process.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            my: 6,
          }}
        >
          <Button
            variant="contained"
            onClick={() => handleComplete(Outcomes.getStarted)}
            sx={{
              minWidth: 180,
              mb: 3,
            }}
          >
            {Outcomes.getStarted}
          </Button>
          <Button
            variant="text"
            onClick={() => handleComplete(Outcomes.alreadyStarted)}
          >
            {Outcomes.alreadyStarted}
          </Button>
        </Box>
      </>
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
